import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";

export type PaymentSettingsConfirmationModalId =
  | "contract-type-change-confirmation"
  | "confirmation-fr-payment-speed"
  | "non-factored-to-factored"
  | "self-finance-no-invoice-to-factored"
  | "self-finance-no-invoice-to-self-finance-with-invoice"
  | "non-factored-bill-out-to-pay-carrier"
  | "self-finance-off-platform-edit";

const NON_FACTORED_CONTRACT_TYPES = [
  FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT,
  FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD
];

const NON_FACTORED_BILL_OUT_TARGET_TYPES = [
  FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE
];

const SELF_FINANCE_WITH_INVOICE_TARGET_TYPES = [
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM
];
const CONTRACT_TYPE_CHANGE_CONFIRMATION_TRANSITIONS = new Set([
  `${FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD}->${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE}`,
  `${FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD}->${FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER}`,
  `${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE}->${FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER}`,
  `${FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER}->${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE}`,
  `${FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD}->${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE}`,
  `${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE}->${FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE}`
]);

interface GetPaymentSettingsConfirmationModalIdArgs {
  previousContractType?: string | null;
  nextContractType?: string | null;
  hasPaymentProfile: boolean;
  fundingRequestStatus?: string | null;
  fundingRequestType?: string | null;
  hasPaymentSpeedMismatch: boolean;
  hasBrokerSplitMismatch: boolean;
  isSelfFinanceOffPlatformEdit?: boolean;
}

export const getPaymentSettingsConfirmationModalId = ({
  previousContractType,
  nextContractType,
  hasPaymentProfile,
  fundingRequestStatus,
  fundingRequestType,
  hasPaymentSpeedMismatch,
  hasBrokerSplitMismatch,
  isSelfFinanceOffPlatformEdit
}: GetPaymentSettingsConfirmationModalIdArgs): PaymentSettingsConfirmationModalId | null => {
  // Editing carrier/shipper fields on an approved off-platform self-finance FR (no contract-type
  // change) takes precedence over the legacy payment-speed confirmation.
  if (isSelfFinanceOffPlatformEdit) {
    return "self-finance-off-platform-edit";
  }

  const isConvertingFromBillOutToPayCarrier =
    previousContractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT &&
    nextContractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_STANDARD;

  if (isConvertingFromBillOutToPayCarrier) {
    return "non-factored-bill-out-to-pay-carrier";
  }

  const isConvertingFromSelfFinanceNoInvoiceToFactored =
    previousContractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE &&
    nextContractType === FUNDING_REQUEST_CONTRACT_TYPES.STD_BROKER;

  if (isConvertingFromSelfFinanceNoInvoiceToFactored) {
    return "self-finance-no-invoice-to-factored";
  }

  const BLOCKED_STATUSES_FOR_SF_NO_INVOICE_TO_SF_WITH_INVOICE = [
    "Approved",
    "Remote Approved",
    "Paid",
    "Closed",
    "HaulPay Review"
  ];
  const isConvertingFromSelfFinanceNoInvoiceToSelfFinanceWithInvoice =
    previousContractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE &&
    SELF_FINANCE_WITH_INVOICE_TARGET_TYPES.includes(nextContractType as string);

  if (isConvertingFromSelfFinanceNoInvoiceToSelfFinanceWithInvoice) {
    if (
      BLOCKED_STATUSES_FOR_SF_NO_INVOICE_TO_SF_WITH_INVOICE.includes(fundingRequestStatus ?? "")
    ) {
      return null;
    }
    return "self-finance-no-invoice-to-self-finance-with-invoice";
  }

  const isConvertingFromBillOut =
    previousContractType === FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT &&
    NON_FACTORED_BILL_OUT_TARGET_TYPES.includes(nextContractType as string);

  if (isConvertingFromBillOut) {
    return "non-factored-to-factored";
  }

  const isContractTypeChange = CONTRACT_TYPE_CHANGE_CONFIRMATION_TRANSITIONS.has(
    `${previousContractType}->${nextContractType}`
  );

  if (isContractTypeChange) {
    return "contract-type-change-confirmation";
  }

  const shouldUseLegacyPaymentSpeedConfirmation =
    hasPaymentProfile &&
    fundingRequestStatus === "Remote Approved" &&
    Boolean(fundingRequestType) &&
    !NON_FACTORED_CONTRACT_TYPES.includes(
      fundingRequestType as (typeof NON_FACTORED_CONTRACT_TYPES)[number]
    ) &&
    (hasPaymentSpeedMismatch || hasBrokerSplitMismatch);

  if (shouldUseLegacyPaymentSpeedConfirmation) {
    return "confirmation-fr-payment-speed";
  }

  return null;
};
